import React, { useEffect, useState } from 'react';
import { Coins, RefreshCw, Sparkles, Waypoints, Zap } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MetricCard = ({ label, value, helper, testId }) => (
  <div className="glass-card p-5" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
    <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
    <p className="mt-2 text-xs text-text-muted">{helper}</p>
  </div>
);

export const AIOptimizationHub = ({ authFetch }) => {
  const [hub, setHub] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadHub = async (force = false) => {
    try {
      setRefreshing(force);
      const res = await authFetch(`${API}/admin/ai-optimization-hub${force ? '/refresh' : ''}`, { method: force ? 'POST' : 'GET' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cargar IA rentable');
      setHub(data);
      if (force) toast.success('Torre de optimización actualizada');
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar IA rentable');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { loadHub(false); }, []);

  if (!hub) return <div className="glass-card p-8 text-center text-text-muted" data-testid="ai-optimization-loading">Cargando control tower de IA...</div>;

  return (
    <div className="space-y-6" data-testid="ai-optimization-hub">
      <div className="glass-card tracing-panel overflow-hidden" data-testid="ai-optimization-hero-card">
        <div className="grid gap-6 p-6 xl:grid-cols-[1.08fr_0.92fr] xl:p-8">
          <div>
            <p className="editorial-kicker mb-3">IA rentable</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Diagnóstico aplicado · control tower de routing, créditos y LLOPS</p>
            <h3 className="mt-3 text-3xl font-semibold text-text-heading">Optimizar aquí ya no es solo bajar coste: es decidir qué stack usa Aynara, qué módulo consume créditos y cómo evoluciona cada loop operativo.</h3>
            <p className="mt-4 max-w-3xl text-sm text-text-body">Este hub se convierte en el puente entre estrategia de modelos, eficiencia económica y expansión del nuevo Neuralia MCP-style OS.</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="ai-optimization-diagnosis-chips">
              <span className="insight-chip">Routing económico</span>
              <span className="insight-chip">Créditos 369</span>
              <span className="insight-chip">LLOPS</span>
              <span className="insight-chip">Módulos MCP</span>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <MetricCard label="Mensajes" value={hub.metrics.chat_messages} helper="últimos 7 días" testId="ai-opt-metric-chat" />
            <MetricCard label="Briefings" value={hub.metrics.briefings} helper="entradas con contexto" testId="ai-opt-metric-briefings" />
            <MetricCard label="Bookings humanos" value={hub.metrics.bookings_human} helper="tratamiento humano inicial" testId="ai-opt-metric-human-bookings" />
            <MetricCard label="Órdenes CEO" value={hub.metrics.ceo_orders} helper="decisiones ejecutivas recientes" testId="ai-opt-metric-ceo-orders" />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={() => loadHub(true)} className="btn-secondary" disabled={refreshing} data-testid="ai-optimization-refresh-button">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Actualizando...' : 'Actualizar torre'}
        </button>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="glass-card p-6" data-testid="ai-opt-routing-card">
          <div className="flex items-center gap-2 text-text-heading"><Waypoints className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Política de routing de Aynara</h4></div>
          <div className="mt-4 space-y-4">
            {hub.routing_guardrails.map((item, index) => (
              <div key={item.layer} className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid={`ai-opt-routing-${index}`}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{item.layer}</p>
                <h5 className="mt-2 text-lg font-semibold text-text-heading">{item.recommended_model}</h5>
                <div className="mt-3 grid gap-3 md:grid-cols-2 text-sm">
                  <div><p className="text-text-heading font-medium mb-2">Usar para</p><div className="flex flex-wrap gap-2">{item.use_for.map((value) => <span key={value} className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{value}</span>)}</div></div>
                  <div><p className="text-text-heading font-medium mb-2">Evitar en</p><div className="flex flex-wrap gap-2">{item.avoid_for.map((value) => <span key={value} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{value}</span>)}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card p-6" data-testid="ai-opt-llops-card">
          <div className="flex items-center gap-2 text-text-heading"><Zap className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">LLOPS como sistema de mejora continua</h4></div>
          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {hub.llops_cycle.map((item, index) => (
              <div key={item} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`ai-opt-llops-step-${index}`}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Paso {index + 1}</p>
                <p className="mt-2 text-sm font-semibold text-text-heading">{item}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-[24px] border border-primary/15 bg-primary/5 p-4" data-testid="ai-opt-weekly-actions-card">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted mb-3">Acciones semanales</p>
            <div className="space-y-2 text-sm text-text-body">{hub.weekly_actions.map((item, index) => <p key={item}>{index + 1}. {item}</p>)}</div>
          </div>
        </div>
      </div>

      <div className="glass-card p-6" data-testid="ai-opt-credit-packs-card">
        <div className="flex items-center gap-2 text-text-heading"><Coins className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Créditos 369 para vender uso operativo, no solo servicios</h4></div>
        <div className="mt-5 grid gap-4 xl:grid-cols-3">
          {(hub.credit_packs || []).map((item) => (
            <div key={item.id} className="rounded-[24px] border border-border-subtle bg-card/40 p-5" data-testid={`ai-opt-credit-pack-${item.id}`}>
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{item.credits} créditos</p>
              <h5 className="mt-2 text-xl font-semibold text-text-heading">{item.name}</h5>
              <p className="mt-3 text-3xl font-bold text-primary">${item.price}</p>
              <p className="mt-3 text-sm text-text-body">{item.best_for}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <div className="glass-card p-6" data-testid="ai-opt-core-modules-card">
          <div className="flex items-center gap-2 text-text-heading"><Sparkles className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Módulos centrales que consumen, coordinan o disparan valor</h4></div>
          <div className="mt-4 space-y-3">
            {(hub.core_modules || []).map((item, index) => (
              <div key={item.name} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`ai-opt-core-module-${index}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-text-heading">{item.name}</p>
                    <p className="mt-1 text-xs text-text-muted">{item.headline}</p>
                  </div>
                  <span className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{item.status}</span>
                </div>
                <p className="mt-3 text-sm text-text-body">{item.next_step}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="glass-card p-6" data-testid="ai-opt-copy-human-card">
            <h4 className="text-lg font-semibold text-text-heading">Checklist de copy humano</h4>
            <div className="mt-4 grid gap-3">
              {hub.human_copy_guardrails.map((item, index) => (
                <div key={item.title} className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid={`ai-opt-copy-guardrail-${index}`}>
                  <p className="text-text-heading font-semibold">{item.title}</p>
                  <p className="mt-2 text-sm text-text-body">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="glass-card p-6" data-testid="ai-opt-cost-watchouts">
            <h4 className="text-lg font-semibold text-text-heading">Watchouts de coste y calidad</h4>
            <div className="mt-4 space-y-2 text-sm text-text-body">{hub.cost_watchouts.map((item, index) => <p key={item}>{index + 1}. {item}</p>)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIOptimizationHub;